/**
 * download-socket.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import * as CRC32 from "crc-32";
/// <reference path="./crc-32.d.ts"/>

import { Observable, Observer, ReplaySubject, Subject } from "rxjs/Rx";

import { oneline } from "../../support/utilities/functions";

import { Connection } from "./connection";
import { ReadyState } from "./enums";

export class DownloadSocket
{
    public incoming : Subject<Blob>;
    public outgoing : Subject<Blob>;
    private socket : WebSocket;
    private initialMessages : Blob[] = [];

    public file : SocketFile = new SocketFile();

    public completion : Subject<SocketFile> = new Subject();

    protected buildConnections () : void
    {
        this.socket = new WebSocket(`${this.baseUrl}${this.publicToken}`);
        this.socket.binaryType = "blob";
        this.incoming = new Subject<Blob>();
        this.outgoing = new Subject<Blob>();

        this.socket.onclose = () => {
            this.incoming.complete();
            console.warn("Socket closed!");
        };
        this.socket.onerror = (err : any) => this.incoming.error(err);
        this.socket.onmessage = (message : any) => {
            if (message.data instanceof Blob)
            {
                this.incoming.next(message.data);
            }
            else
            {
                console.error("Received non-blob message", message);
            }
        };
        this.socket.onopen = () => {
            this.initialMessages.forEach((b : Blob) => this.socket.send(b));
            this.initialMessages = [];
        };

        this.outgoing.subscribe((b : Blob) => {
            if (this.socket.readyState === ReadyState.Open)
            {
                this.socket.send(b);
            }
            else
            {
                this.initialMessages.push(b);
            }
        });
    }

    public static BL_TOKEN : number = 32;
    public static BL_SECRET : number = 256;

    protected buildTokenMessage () : Blob
    {
        const token : Uint8Array = new Uint8Array(DownloadSocket.BL_SECRET);
        for (let i : number = 0; i < DownloadSocket.BL_SECRET; i++)
        {
            token[i] = Number.parseInt(
                this.secretToken.slice(2 * i, 2 * (i + 1)),
                16
            );
        }
        return new Blob([token]);
    }

    public constructor (
        private baseUrl : string,
        private publicToken : string,
        private secretToken : string,
        filetype? : string
    )
    {
        if (secretToken.length !== DownloadSocket.BL_SECRET * 2
            || publicToken.length !== DownloadSocket.BL_TOKEN * 2)
        {
            throw new Error(`Tokens have incorrect lengths `
                + `(${secretToken.length}+${publicToken.length})`);
        }
        this.buildConnections();
        let i : number = 0;
        this.file.type = filetype;
        this.incoming.subscribe(
            (b : Blob) => {
                this.file.addBlob(b);
                i++;
            },
            (err : any) => console.error(`Download socket error:`, err),
            () => {
                this.file.complete();
                this.completion.next(this.file);
                this.completion.complete();
            }
        );
        const tokenMessage : Blob = this.buildTokenMessage();
        this.outgoing.next(tokenMessage);
    }
}

class QueuingBufferReader
{
    private reader : FileReader = new FileReader();
    private reading : boolean = false;
    private toRead : Blob[] = [];
    private readCount : number = 0;
    public crc : number = 0;

    constructor ()
    {
        this.reader.onload = () => this.finishRead();
    }

    public out : Subject<ArrayBuffer> = new Subject();

    protected readNext () : void
    {
        const b : Blob = this.toRead.shift();
        if (b === null)
        {
            console.warn("buffer reader finished");
            this.reading = false;
            this.out.complete();
            return;
        }
        if (!(b instanceof Blob))
        {
            console.error(b, typeof b);
        }
        this.reading = true;
        this.readCount++;
        this.reader.readAsArrayBuffer(b);
    }

    protected finishRead () : void
    {
        this.out.next(this.reader.result);
        this.crc = CRC32.buf(new Uint8Array(this.reader.result), this.crc);
        if (this.toRead.length > 0)
        {
            this.readNext();
        }
        else
        {
            this.reading = false;
        }
    }

    public read (blob : Blob) : void
    {
        this.toRead.push(blob);
        if (!this.reading)
        {
            this.readNext();
        }
    }

    public complete () : void
    {
        this.read(null);
    }
}

function debugObserver (name : string, useNext : boolean = true) : Observer<any>
{
    return {
        next: useNext ? (val) => console.warn(`${name}:`, val) : () => null,
        error: (err) => console.warn(`${name}: error`, err),
        complete: () => console.warn(`${name}: complete!`),
    };
}

export class SocketFile
{
    protected chunks : Blob[] = [];

    public type : string;

    public size : number;

    public file : Blob;

    public done : boolean = false;

    public hasBOM : boolean = false;

    protected bufferizer : QueuingBufferReader = new QueuingBufferReader();

    public crc : number;
    public crcHex : string;

    protected finalCRC : Observable<number> = this.bufferizer.out.last().map(
        () => this.bufferizer.crc).do((crc : number) => {
            console.warn("CRC calculated", crc);
            this.crc = crc;
            this.crcHex = `0x${this.crc.toString(16)}`;
        });

    constructor () {
        this.bufferizer.out.subscribe(debugObserver("bufferizer", false));
        this.finalCRC.subscribe(debugObserver("final CRC"));
    }

    public emptyCount : number = 0;
    public bufferCount : number = 0;

    public addBlob (b : Blob) : void
    {
        if (b.size === 0)
        {
            this.emptyCount++;
        }
        else
        {
            this.chunks.push(b);
            this.bufferizer.read(b);
        }
    }

    protected forCheck : Subject<Blob> = new Subject();

    public complete () : void
    {
        if (!this.done)
        {
            this.bufferizer.complete();
            this.file = new Blob(this.chunks, {type: this.type});
            this.size = this.file.size;
            this.bufferCount = this.chunks.length;
            this.chunks = [];
            this.done = true;

            if (this.emptyCount > 0)
            {
                console.error(
                    `Download socket received ${this.emptyCount} empty frames!`);
            }
        }
    }

    public generateUrl () : string
    {
        this.complete();
        return URL.createObjectURL(this.file);
    }

    // public flatCRC : number;
    // protected getCRC () : Observable<number>
    // {
    //     return Observable.create((o : Observer<number>) => {
    //         const reader : FileReader = new FileReader();
    //         reader.onload = () => {
    //             const crc : number =
    //                 CRC32.buf(new Uint8Array(reader.result));
    //             this.flatCRC = crc;
    //             o.next(crc);
    //             o.complete();
    //         };
    //         reader.readAsArrayBuffer(this.file);
    //     });
    // }

    public verify (serverFilesize : number, serverCRC : number)
        : Observable<boolean>
    {
        if (!this.done)
        {
            throw new Error("Cannot verify before download is done");
        }
        else
        {
            if (this.file.size !== serverFilesize)
            {
                console.error(oneline`
                    Server said file would be ${serverFilesize} bytes,
                    got ${this.file.size} bytes`);
                return Observable.of(false);
            }

            // If the CRC calculation is already done.
            // TODO: This logic should be doable with some sort of BehaviorSubj
            // or connectable or something where we don't have to check if the
            // value is already done
            if (this.crc)
            {
                console.log("performing *IMMEDIATE* CRC validation");
                if (this.crc !== serverCRC)
                {
                    console.error(oneline`
                        File checksums did not match. Client got ${this.crc}
                        while server sent ${serverCRC}.`);
                    return Observable.of(false);
                }
                return Observable.of(true);
            }

            console.warn("requesting delayed CRC validation");
            // If the CRC calculation is *not* done, we have to wait for it
            return this.finalCRC.map((localCRC : number) => {
                console.warn("performing delayed CRC validation");
                if (localCRC !== serverCRC)
                {
                    console.error(oneline`
                        File checksums did not match. Client got ${localCRC}
                        while server sent ${serverCRC}.`);
                    return false;
                }
                return true;
            });
        }
    }

    public static readonly BOM : string = "\uFEFF";
    public static readonly BOMByteLength : number = 3;

    public addBOM () : void
    {
        if (!this.done)
        {
            throw new Error(`Can only add BOM after download is complete!`);
        }
        if (this.hasBOM)
        {
            throw new Error(`File already has BOM!`);
        }

        this.file = new Blob([SocketFile.BOM, this.file]);
        this.size += SocketFile.BOMByteLength;
        this.hasBOM = true;
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/server/connection/download-socket.ts